import React, { useEffect, useState } from 'react';
import Dropdown, { DropdownItem } from '@bexs/dollar/components/Dropdown';
import Button from '@bexs/dollar/components/Button';
import Typography from '@bexs/dollar/components/Typography';
import { SvgExport } from '@bexs/dollar-icons';
import { useDispatch } from 'react-redux';
import exportActions, { clearList } from 'store/ducks/exports/actions';
import fileGenerationActions from 'store/ducks/file-generation/actions';
import PDFIcon from 'static/images/pdf.png';
import CSVIcon from 'static/images/csv.png';
import { useSelector } from 'store';
import { PageOverlayLoader, ExportLoaderContainer } from 'pages/Balance/styles';
import { Loader } from '../../../../components/Loader';
import { SupportedFilesType } from 'store/ducks/file-generation/types';
import dayjs from 'dayjs';

const ExportSelect: React.FC = () => {
  const dispatch = useDispatch();

  const { isStatementListFilled, previousDayValue } = useSelector().statements;
  const { periodStart, periodEnd, document, reportType } = useSelector().filter;
  const [selectedFileType, setSelectedFileType] = useState<
    SupportedFilesType | undefined
  >(undefined);

  // theres a time gap between the api and file generation request
  // thus this state was create to keep a continuity between loadings
  const [isLoadingDone, setIsLoadingDone] = useState(false);

  const {
    isLoading: statementsLoading,
    error: statementsError,
    statements,
  } = useSelector().exports;

  const { isLoading: fileGenerationLoading, error: fileGenerationError } =
    useSelector().fileGeneration;

  const exportButtonTrigger = (
    <Button
      style={{ marginRight: '16px ' }}
      disabled={!isStatementListFilled}
      variation="secondary-outline"
      size="small"
      icon={<SvgExport />}
    >
      Export
    </Button>
  );

  const fetchStatements = () => {
    dispatch(
      exportActions.request({
        periodStart: dayjs(periodStart).format('YYYY-MM-DD'),
        periodEnd: periodEnd
          ? dayjs(periodEnd).format('YYYY-MM-DD')
          : undefined,
        document,
        reportType,
      })
    );
  };

  const handleFileExport = (fileType: SupportedFilesType) => {
    setSelectedFileType(fileType);
    fetchStatements();
    setIsLoadingDone(false);
  };

  const exportFile = (fileType: SupportedFilesType) => {
    dispatch(
      fileGenerationActions.request({
        statements,
        fileType,
        previousDayValue: previousDayValue.toString(),
        periodStart: dayjs(periodStart).format('YYYY-MM-DD'),
      })
    );
  };

  useEffect(() => {
    if (selectedFileType && statements.length > 0) {
      exportFile(selectedFileType);
    }
  }, [statements, selectedFileType]);

  useEffect(() => {
    if (!fileGenerationLoading) {
      setIsLoadingDone(true);
      dispatch(clearList());
    }
  }, [fileGenerationLoading]);

  useEffect(() => {
    if (fileGenerationError || statementsError) {
      alert('There was an error while exporting the file. Please try again.');
    }
  }, [fileGenerationError, statementsError]);

  const isLoading =
    statementsLoading || fileGenerationLoading || !isLoadingDone;

  return (
    <>
      <div>
        <PageOverlayLoader data-testid="page-overlay" isShowing={isLoading} />
        <ExportLoaderContainer
          data-testid="export-overlay"
          isShowing={isLoading}
        >
          <Loader data-testid="loader" />
          <Typography style={{ whiteSpace: 'pre-wrap' }} as="h5">
            {fileGenerationLoading
              ? `Generating your ${selectedFileType}.\nWait just a few more seconds.`
              : 'Wait while your statement is being downloaded.'}
          </Typography>
        </ExportLoaderContainer>
      </div>

      <Dropdown
        data-testid="dropdown-export"
        centerContent
        trigger={exportButtonTrigger}
      >
        <DropdownItem
          data-testid="pdf-button"
          onClick={() => handleFileExport('pdf')}
        >
          <img src={PDFIcon} alt="PDF Icon" />
          PDF
        </DropdownItem>
        <DropdownItem
          data-testid="csv-button"
          onClick={() => handleFileExport('csv')}
        >
          <img src={CSVIcon} alt="CSV Icon" />
          CSV
        </DropdownItem>
      </Dropdown>
    </>
  );
};

export default ExportSelect;
